import { Popover } from 'antd';
import { useGetCustomStyles } from 'api/hooks';
import { webAppAPI } from 'api/web-app';
import { ReactComponent as DefaultCompanyLogo } from 'assets/defaultImages/CompanyLogo.svg';
import DefaultBanner from 'assets/defaultImages/GenericBanner.png';
import { ReactComponent as ViewPasswordIcon } from 'assets/view-icon.svg';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { colors } from 'styles/colors';
import * as yup from 'yup';
import './ChangePassword.css';
import { FailureModal } from './FailureModal';
import { SoloButton } from './SoloButton';
import { SoloFormInput } from './SoloFormField';
import { SuccessModal } from './SuccessModal';

const smallNavLinkStyles = {
  color: colors.septemberGrey
};

const initialValues = {
  newPassword: '',
  repeatPassword: ''
};
const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required('Please choose a password')
    .min(8, 'Must be at least 8 characters long')
    .matches(/(?=.*[0-9])/, 'Must contain at least one number')
    .matches(/^.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?].*$/, 'Need one special character'),

  repeatPassword: yup
    .string()
    .required('Passwords must match')
    .oneOf([yup.ref('newPassword'), null], `Passwords don't match`)
});

const PasswordRequirements = () => {
  return (
    <div className='password-req-container'>
      <Popover
        key='#edf4ff'
        color='#edf4ff'
        placement='bottom'
        content={
          <div className='p-4 text-blue-700 border border-blue-700 '>
            <div className='font-medium '>Password Requirements</div>
            <ul id='password-requirements' className='pl-2 list-disc'>
              <li>Password must be at least 8 characters long</li>
              <li>Password must contain at least one number, one letter</li>
              <li>Password must contain at least one special character (i.e. !&*)</li>
              <li>Password should not use consecutive numbers (i.e. 12345)</li>
            </ul>
          </div>
        }>
        <div className='flex items-center py-1 cursor-pointer'>
          <svg
            aria-hidden='true'
            className='w-4 h-4 mr-2 text-gray-700 fill-current'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'>
            <path d='M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z' />
          </svg>
          <title className='font-medium' id='view-password-requirements-title'>
            View Password Requirements
          </title>
        </div>
      </Popover>
    </div>
  );
};

export const ChangePassword = () => {
  const routerHistory = useHistory();
  const [showPassword, setShowPassword] = React.useState(false);
  const [resetPasswordResolved, setResetPasswordResolved] = React.useState<{
    wasSuccessful: boolean;
    errorMessage?: string;
  } | null>(null);

  const { customStyles, customStylesError } = useGetCustomStyles();

  React.useEffect(() => {
    document.title = 'Portal - Change password';
  }, []);

  let innerFormik: FormikProps<any> | null = null;
  const keyPressed = (event: React.KeyboardEvent<Element>) => {
    if (innerFormik?.isValid && event.key === 'Enter') {
      attemptReset(innerFormik.values);
    }
  };

  const attemptReset = async (values: typeof initialValues) => {
    await webAppAPI
      .changePassword({ newPassword: values.newPassword, currentPassword: values.repeatPassword })
      .then(result => {
        if (result) {
          routerHistory.push({ pathname: '/' });
        } else {
          innerFormik?.resetForm();
        }
        setResetPasswordResolved({ wasSuccessful: !!result, errorMessage: 'Problems resetting' });

        setTimeout(() => {
          setResetPasswordResolved(null);
        }, 3100);
      });
  };
  return (
    <div className='change-password-container'>
      <div className='background-image-holder'>
        {!!customStyles?.bannerUrl ? (
          <img className='object-cover ' src={customStyles?.bannerUrl || DefaultBanner} alt={''} />
        ) : (
          <img className='object-cover ' src={DefaultBanner} alt={''} />
        )}
      </div>
      <div className='background-image-holder-screen' />

      <div className='modal-holder' role='main'>
        <div className='modal-card'>
          <div className='logo-holder'>
            {!!customStyles?.logoUrl ? <img src={customStyles?.logoUrl} alt={''} /> : <DefaultCompanyLogo title='' />}
          </div>
          <h1 className='modal-title'>Change Your Password</h1>

          <div className='modal-sub-information'>
            Since this is the first time you are logging in, you are required to change your password for security
            purposes.
          </div>

          <Formik<typeof initialValues>
            initialValues={initialValues}
            onSubmit={attemptReset}
            validationSchema={validationSchema}
            render={formik => {
              innerFormik = formik;

              return (
                <div className='form-container'>
                  <div className='form-row'>
                    <div className='relative col-span-3 mt-2 mr-4'>
                      <button
                        aria-label={`${showPassword ? 'Hide' : 'Show'} passwords`}
                        className='absolute cursor-pointer bottom-7 right-4'
                        onClick={() => setShowPassword(!showPassword)}>
                        <ViewPasswordIcon
                          className={`${showPassword ? 'fill-current text-blue-600gloo' : ''}`}
                          title=''
                        />
                      </button>
                      <SoloFormInput
                        id='new-password'
                        autoComplete='new-password'
                        aria-describedby='password-requirements'
                        name='newPassword'
                        placeholder='New Password'
                        type={showPassword ? 'text' : 'password'}
                        onKeyPress={(evt: React.KeyboardEvent<Element>) => keyPressed(evt)}
                      />
                    </div>
                  </div>
                  <div className='form-row'>
                    <div className='relative col-span-3 mt-2 mr-4'>
                      <button
                        aria-label={`${showPassword ? 'Hide' : 'Show'} passwords`}
                        className='absolute cursor-pointer bottom-7 right-4'
                        onClick={() => setShowPassword(!showPassword)}>
                        <ViewPasswordIcon
                          className={`${showPassword ? 'fill-current text-blue-600gloo' : ''}`}
                          title=''
                        />
                      </button>
                      <SoloFormInput
                        name='repeatPassword'
                        placeholder='Confirm Password'
                        type={showPassword ? 'text' : 'password'}
                        autoComplete='new-password'
                        onKeyPress={(evt: React.KeyboardEvent<Element>) => keyPressed(evt)}
                      />
                    </div>
                  </div>

                  <SoloButton
                    text='Change My Password'
                    disabled={(!formik.isValid && formik.dirty) || !formik.dirty}
                    onClick={formik.handleSubmit}
                  />
                </div>
              );
            }}
          />
          <PasswordRequirements />
        </div>
      </div>

      <SuccessModal
        visible={!!resetPasswordResolved?.wasSuccessful}
        successMessage={
          <div>
            Password changed sucessfully!{' '}
            <div>
              You will now be redirected to the{' '}
              <Link style={smallNavLinkStyles} to='/'>
                home page
              </Link>
              .
            </div>
          </div>
        }
      />

      <FailureModal
        visible={!!resetPasswordResolved && !resetPasswordResolved.wasSuccessful}
        failureMessage={<div>Unable to change password.</div>}
        minorMessage={resetPasswordResolved?.errorMessage}
      />
    </div>
  );
};
